import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
} from '../../../components/CommandControlComponents'
import { Warning } from '../../../components/DocsComponents'

const Permissions = () => (
  <Page
    title="Permissions and Audit"
    intro="Command & Control access is controlled by a CAD access level set on each staff member's employee record. There are three tiers — Viewer, Dispatcher (with Call Taker at the same tier), and Commander — and every control-room action is checked against them on the server."
  >
    <Section title="CAD access levels">
      <Bullets>
        <Bullet>
          <strong>Viewer</strong> — read-only access. Viewers can open control
          rooms and watch the live board, but cannot change anything.
        </Bullet>
        <Bullet>
          <strong>Call Taker</strong> and <strong>Dispatcher</strong> — the
          operational tier. Both roles sit at the same level and can run
          day-to-day dispatch: incidents, resources, crew, messaging, and
          command logs.
        </Bullet>
        <Bullet>
          <strong>Commander</strong> — everything above, plus control-room
          lifecycle and formal command decisions.
        </Bullet>
        <Bullet>
          <strong>Site administrators</strong> bypass CAD role checks and are
          treated as Commanders throughout Command & Control.
        </Bullet>
      </Bullets>
      <Info>
        A staff member's CAD access level is set on their employee record. If
        someone has no CAD access level at all, they cannot open the control
        room screens.
      </Info>
    </Section>

    <Section title="What Viewers can do">
      <Bullets>
        <Bullet>Open the Command & Control dashboard and enter any control room.</Bullet>
        <Bullet>Watch the live board — incidents, resources, maps, treatment summaries, and metrics all update normally.</Bullet>
        <Bullet>Open incidents and read their incident logs.</Bullet>
        <Bullet>Read Control Logs and the complex-decision register.</Bullet>
        <Bullet>Read crew message threads (opening a thread marks it as read for the whole room).</Bullet>
      </Bullets>
      <Info>
        Viewers see an amber <strong>View Only</strong> chip in the control-room
        header, and their role label appears under the operator name. Buttons
        that would change anything — New Incident, New Log, resource status,
        crew assignment, the chat composer, and incident edit controls — are
        hidden or disabled.
      </Info>
    </Section>

    <Section title="What Dispatchers and Call Takers can do">
      <Bullets>
        <Bullet>Everything a Viewer can do, plus all operational changes.</Bullet>
        <Bullet>Create incidents, update incident details, and record call triage.</Bullet>
        <Bullet>Add incident log entries and transfer notes to the tactical command log.</Bullet>
        <Bullet>Add, rename, update, move, and remove resources.</Bullet>
        <Bullet>Change resource status, manage breaks, and edit resource times.</Bullet>
        <Bullet>Assign and remove crew members on resources.</Bullet>
        <Bullet>Assign resources to incidents and stand them down.</Bullet>
        <Bullet>Request location sharing from crew members.</Bullet>
        <Bullet>Write command logs from the New Log action.</Bullet>
        <Bullet>Send messages to resources from the chat drawer.</Bullet>
        <Bullet>Add, edit, move, and delete named map pins on the event map.</Bullet>
        <Bullet>Assign patients to treatment zones from the control room.</Bullet>
      </Bullets>
    </Section>

    <Section title="What only Commanders can do">
      <Bullets>
        <Bullet>Start a control room — from an event or as a standalone custom room.</Bullet>
        <Bullet>Close a control room and reopen a closed one.</Bullet>
        <Bullet>Upload and manage the event map, including scaling and positioning.</Bullet>
        <Bullet>Upload, rename, recolour, and delete GPX routes for the GPX map view.</Bullet>
        <Bullet>Record complex decisions and open their linked action details.</Bullet>
        <Bullet>Create treatment centres from the control room.</Bullet>
      </Bullets>
    </Section>

    <Section title="Server-side enforcement">
      <Warning>
        The interface hides controls the signed-in role cannot use, but the
        permission check itself happens on the server for every request. Even
        if a button were visible, the server rejects any action above the
        user's CAD tier.
      </Warning>
    </Section>

    <Section title="Audit trail">
      <Bullets>
        <Bullet>Command logs capture explicit operator entries.</Bullet>
        <Bullet>Incident logs capture incident-specific changes and links.</Bullet>
        <Bullet>System logs capture important automatic or structural changes such as resource status changes, resource renames, crew changes, location-sharing requests, event map updates, and manual map position updates.</Bullet>
        <Bullet>Complex decisions create structured records for major operational decisions and write a Decision entry into the command log.</Bullet>
      </Bullets>
      <Info>
        Encourage operators to use command logs for operational context and
        rely on system/incident logs for traceable state changes.
      </Info>
    </Section>

    <Section title="Closing and reopening">
      <Bullets>
        <Bullet>A commander can close a control room from Control Room Info.</Bullet>
        <Bullet>The system blocks closure while treatment locations still contain active patients.</Bullet>
        <Bullet>Closing a room releases its resources: crews are unassigned, statuses are set to Off Duty, location-sharing consents are cleared, and the call signs become free for other rooms or future events to reuse.</Bullet>
        <Bullet>The closed board still shows what the room ran, and a system log records how many resources were released.</Bullet>
        <Bullet>Closed rooms show an information banner and can be reopened from Control Room Info. Reopening re-claims any of the room's resources that are still free and re-adds vehicles deployed to the linked event.</Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default Permissions
